"use client";

/**
 * Searchable, SORTABLE card grid for the residences hub. Search is
 * client-side over the already-fetched list (~150 rows - no round
 * trips) and folds Azerbaijani diacritics so "qelebe" finds "Qələbə".
 * The shared SortPicker (same control as /trends + listings) reorders
 * a single flat grid - name, AZN/m², or activity - so the order is
 * always explicit rather than an opaque district grouping. Each card
 * carries its district + an image slot (most residences have no
 * admin-hosted image yet, so the gradient fallback must read as
 * designed, not broken).
 *
 * The grid draws PAGE cards and PAGE more each time its end comes near
 * (2026-10-27): all 431 cards made a 966 KB page whose hydration held a
 * desktop's main thread 0.5-1.5 s (Google: desktop 69-83). Every
 * residence stays one link away for readers and crawlers in the A-Z
 * index under the grid.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import Link from "@/lib/localized-link";
import type { Locale } from "@/lib/i18n-config";
import { t as translate, type TranslationKey } from "@/lib/i18n";
import { formatMonthDayShort } from "@/lib/blog-format";
import { Search } from "lucide-react";
import SortPicker, { type SortOption } from "@/components/SortPicker";
import ResidenceRequestForm from "@/components/residence/ResidenceRequestForm";

import ResidenceCard from "@/components/residences/ResidenceCard";
type SortKey = "name" | "price_desc" | "price_asc" | "activity";

/** Cards drawn at first and added each time the grid's end comes near:
 *  fills 2, 3 and 4 columns evenly. */
const PAGE = 48;

// Same control + labels as the /trends hub sort, so sorting feels
// identical across the two hubs.
const SORTS: SortOption[] = [
  { value: "name", key: "trends.index.sort.name" as TranslationKey },
  { value: "price_desc", key: "listings.sort.price_desc" as TranslationKey },
  { value: "price_asc", key: "listings.sort.price" as TranslationKey },
  { value: "activity", key: "trends.index.sort.activity" as TranslationKey },
];

function sortResidences(rows: ResidenceRow[], key: SortKey): ResidenceRow[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    switch (key) {
      case "price_desc": {
        const av = a.median_ppm ?? -1;
        const bv = b.median_ppm ?? -1;
        return bv - av || a.name.localeCompare(b.name);
      }
      case "price_asc": {
        const av = a.median_ppm ?? Number.POSITIVE_INFINITY;
        const bv = b.median_ppm ?? Number.POSITIVE_INFINITY;
        return av - bv || a.name.localeCompare(b.name);
      }
      case "activity": {
        // Live on-market total; the comparables cohort covers rows
        // that predate the live counters (or a degraded backend).
        const av = (a.live_sale_n ?? 0) + (a.live_rent_n ?? 0) || (a.listing_count ?? 0);
        const bv = (b.live_sale_n ?? 0) + (b.live_rent_n ?? 0) || (b.listing_count ?? 0);
        return bv - av || a.name.localeCompare(b.name);
      }
      case "name":
      default:
        return a.name.localeCompare(b.name);
    }
  });
  return copy;
}

export interface ResidenceRow {
  cluster_id: string;
  name: string;
  /** Popular alternative names - searched alongside the name. */
  aliases?: string[] | null;
  district: string | null;
  median_ppm: number | null;
  median_price: number | null;
  /** Comparables cohort size - stats context only. */
  listing_count: number | null;
  /** Live on-market counts - same numbers the residence page shows. */
  live_sale_n?: number | null;
  live_rent_n?: number | null;
  /** False for residences below the stats floor - their page is noindex. */
  indexable?: boolean;
  /** Where median_ppm comes from: the weekly comparables ("cohort"), the
   *  live priced sale listings ("live"), or nothing yet (null). */
  price_basis?: "cohort" | "live" | null;
  price_n?: number | null;
  /** Listings this residence had that are gone now + when the newest of
   *  them was last seen (ISO date). */
  previous_n?: number | null;
  last_listing_at?: string | null;
  lat?: number | null;
  lng?: number | null;
  image_url?: string | null;
}

function fold(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ə/g, "e")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s").replace(/ç/g, "c").replace(/ğ/g, "g")
    .replace(/ö/g, "o").replace(/ü/g, "u");
}

// Relevance score for a residence against a folded multi-word query.
// Returns -1 when not every query word is found somewhere (name /
// aliases / district) - i.e. an AND match so "yasamal park" needs both
// words, in any order. Among matches, rank: name starts-with the full
// query > name contains it > a word hits an alias > only district.
// This makes typing a couple of words surface the obvious residence
// first instead of an arbitrary catalog-order hit.
// Bounded Levenshtein - returns early once distance exceeds `max`, so
// it's cheap for the typo fallback (we only care about <= 2 edits).
function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > max) return max + 1;
    prev = cur;
  }
  return prev[b.length];
}

// One query word "fuzzily" matches a residence when some token of its
// name/aliases is a typo away (edit distance scaled to word length).
function wordFuzzyHits(word: string, tokens: string[]): boolean {
  const max = word.length <= 4 ? 1 : 2;
  return tokens.some(
    (tok) => tok.includes(word) || editDistance(word, tok, max) <= max,
  );
}

function searchScore(r: ResidenceRow, words: string[], full: string): number {
  if (words.length === 0) return 0;
  const name = fold(r.name);
  const aliases = (r.aliases ?? []).map(fold);
  const district = r.district ? fold(r.district) : "";
  const hay = [name, ...aliases, district].join(" ");
  // AND gate: every query word must appear somewhere.
  if (!words.every((w) => hay.includes(w))) return -1;
  let score = 1;
  if (name.startsWith(full)) score += 100;
  else if (name.includes(full)) score += 50;
  if (words.every((w) => name.includes(w))) score += 20;
  else if (words.some((w) => aliases.some((a) => a.includes(w)))) score += 10;
  // Shorter names rank a touch higher on an otherwise-equal match
  // ("Park Azure" over "Park Azure Residence Premium" for "park").
  score += Math.max(0, 40 - name.length) / 100;
  return score;
}

function fmt(n: number | null | undefined): string {
  if (!n) return "-";
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

function HubResidenceCard({ r, locale, rank }: { r: ResidenceRow; locale: Locale; rank?: number }) {
  return (
    <ResidenceCard
      id={r.cluster_id}
      name={r.name}
      district={r.district}
      image={r.image_url}
      rank={rank}
      value={r.median_ppm == null ? (
        /* No price yet: neither the weekly comparables nor three live
           priced listings. Said outright, and the live counts still show
           - a residence is never a blank card (ticket 34, 2026-10-18). */
        <span className="text-[11px] text-gray-400">
          {translate("residences.card.noprice", locale)}
        </span>
      ) : (
        <span className="text-sm font-bold text-gray-900 tabular-nums">
          {fmt(r.median_ppm)} <span className="text-[10px] font-medium text-gray-500">AZN/m²</span>
        </span>
      )}
      // Live on-market counts - the number a visitor actually finds after
      // clicking through, matching the residence page's own header. Zero
      // on market is said outright: the sold-out state is information.
      meta={(r.live_sale_n ?? 0) > 0 && (r.live_rent_n ?? 0) > 0
        ? translate("residences.card.live_both", locale)
            .replace("{sale}", String(r.live_sale_n))
            .replace("{rent}", String(r.live_rent_n))
        : (r.live_sale_n ?? 0) > 0
          ? translate("residences.card.live_sale", locale).replace("{n}", String(r.live_sale_n))
          : (r.live_rent_n ?? 0) > 0
            ? translate("residences.card.live_rent", locale).replace("{n}", String(r.live_rent_n))
            : r.last_listing_at
              ? translate("residences.card.last_listing", locale)
                  .replace("{date}", formatMonthDayShort(Date.parse(r.last_listing_at), locale))
              : translate("residences.card.quiet", locale)}
    />
  );
}

export default function ResidencesIndexClient({
  locale,
  residences,
  initialSort = "name",
  ranked = false,
  azIndex = true,
}: {
  locale: Locale;
  residences: ResidenceRow[];
  /** Starting order; the ranking page opens on price, high to low. */
  initialSort?: SortKey;
  /** Show each residence's place in the price order ("#1") on its card. */
  ranked?: boolean;
  /** The A-Z index of every residence under the grid (the hub's; a
   *  ranking page leaves it to the hub). */
  azIndex?: boolean;
}) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>(initialSort);
  // Rank = place in the price order, whatever sort the visitor picks.
  const rankOf = useMemo(() => {
    if (!ranked) return null;
    return new Map(sortResidences(residences, "price_desc").map((r, i) => [r.cluster_id, i + 1]));
  }, [ranked, residences]);

  // How many cards are drawn; back to one page when the search or the
  // order changes.
  const [limit, setLimit] = useState(PAGE);
  const [limitFor, setLimitFor] = useState(`${query}|${sortKey}`);
  if (limitFor !== `${query}|${sortKey}`) {
    setLimitFor(`${query}|${sortKey}`);
    setLimit(PAGE);
  }
  const moreRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    const q = fold(query.trim());
    if (!q) return sortResidences(residences, sortKey);
    // Smart search: multi-word AND across name/aliases/district, then
    // rank by relevance. The chosen sort is the tiebreaker within an
    // equal relevance score, so "name" still reads alphabetically but
    // the best textual matches always lead.
    const words = q.split(/\s+/).filter(Boolean);
    let scored = residences
      .map((r) => ({ r, s: searchScore(r, words, q) }))
      .filter((x) => x.s >= 0);
    // Typo fallback: only when the strict pass found nothing, so good
    // queries are never diluted. Each query word must fuzzy-hit a
    // name/alias token (AND), e.g. "residance" -> "...Residence".
    if (scored.length === 0) {
      scored = residences
        .filter((r) => {
          const toks = [
            ...fold(r.name).split(/\s+/),
            ...(r.aliases ?? []).flatMap((a) => fold(a).split(/\s+/)),
          ].filter(Boolean);
          return words.every((w) => wordFuzzyHits(w, toks));
        })
        .map((r) => ({ r, s: 0 }));
    }
    const bySort = sortResidences(scored.map((x) => x.r), sortKey);
    const scoreOf = new Map(scored.map((x) => [x.r.cluster_id, x.s]));
    return [...bySort].sort(
      (a, b) => (scoreOf.get(b.cluster_id)! - scoreOf.get(a.cluster_id)!),
    );
  }, [query, residences, sortKey]);

  useEffect(() => {
    const el = moreRef.current;
    if (!el || limit >= filtered.length) return;
    const io = new IntersectionObserver(
      (es) => { if (es.some((e) => e.isIntersecting)) setLimit((n) => n + PAGE); },
      { rootMargin: "800px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [limit, filtered.length]);

  // The A-Z index: every residence, a plain link each (no image, no card).
  const byName = useMemo(() => sortResidences(residences, "name"), [residences]);

  return (
    <>
      {/* Search + sort row - same shape as the /trends hub: search grows
          on the left, the shared SortPicker sits at the right edge. */}
      <div className="mt-6 flex items-center justify-between gap-2">
        <div className="relative max-w-xs flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={translate("listings.location.search", locale)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 bg-white text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="hidden sm:inline text-sm text-gray-500 tabular-nums">
            {translate("residences.search.count", locale).replace("{n}", String(filtered.length))}
          </span>
          <SortPicker
            value={sortKey}
            onChange={(v) => setSortKey(v as SortKey)}
            options={SORTS}
          />
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-400">
            {translate("listings.location.empty", locale)}
          </p>
          {/* Empty search = a residence we might be missing. Offer to
              request it (anon allowed, one open request per visitor). */}
          <ResidenceRequestForm defaultName={query.trim()} locale={locale} />
        </div>
      ) : (
        <ul className="mt-6 grid grid-cols-1 gap-2 sm:mt-8 sm:grid-cols-3 sm:gap-3 lg:grid-cols-4">
          {filtered.slice(0, limit).map((r) => (
            <li key={r.cluster_id} className="flex [contain-intrinsic-size:0_76px] [content-visibility:auto] sm:[contain-intrinsic-size:0_200px]">
              <HubResidenceCard r={r} locale={locale} rank={rankOf?.get(r.cluster_id)} />
            </li>
          ))}
        </ul>
      )}
      {limit < filtered.length && <div ref={moreRef} className="h-px" aria-hidden />}

      {azIndex && <section className="mt-12" aria-labelledby="residences-az">
        <h2 id="residences-az" className="text-sm font-semibold text-gray-900">
          {translate("residences.az.title", locale)}
        </h2>
        <ul className="mt-3 columns-2 gap-x-6 text-sm sm:columns-3 lg:columns-4">
          {byName.map((r) => (
            <li key={r.cluster_id} className="break-inside-avoid py-0.5">
              <a href={`/${locale}/residence/${encodeURIComponent(r.cluster_id)}`} className="text-gray-600 underline-offset-2 hover:text-gray-900 hover:underline">
                {r.name}
              </a>
            </li>
          ))}
        </ul>
      </section>}
    </>
  );
}
